import React, { useState, useCallback, createContext, useContext, ReactNode, useRef } from 'react';
import { useAddress } from './AddressContext';
import { pollingService } from '../services/PollingService';

// Address Affordance Context 타입 정의
interface AddressAffordanceContextType {
  showAddressAffordance: boolean;
  showIconPulse: boolean;
  checkAddressAndExecute: (action: () => void, affordanceType?: 'address' | 'cart') => void;
  dismissAffordance: () => void;
  isAddressSet: boolean;
  isPollingActive: boolean;
  affordanceType: 'address' | 'cart';
  showAddressSetSuccessAffordance: () => void;
  isSuccessAffordance: boolean;
}

// Context 생성
const AddressAffordanceContext = createContext<AddressAffordanceContextType | undefined>(undefined);

// Provider 컴포넌트
export const AddressAffordanceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAddressSet } = useAddress();
  const [showAddressAffordance, setShowAddressAffordance] = useState(false);
  const [showIconPulse, setShowIconPulse] = useState(false);
  const [affordanceType, setAffordanceType] = useState<'address' | 'cart'>('address');
  const [isSuccessAffordance, setIsSuccessAffordance] = useState(false);
  
  // 중복 호출 방지를 위한 ref
  const isAffordanceActiveRef = useRef(false);

  // 폴링 상태 확인
  const isPollingActive = pollingService.isCurrentlyPolling();

  // 주소 설정 여부 확인
  const checkAddressAndExecute = useCallback((action: () => void, type: 'address' | 'cart' = 'address') => {
    // 폴링 중일 때는 어포던스 표시
    if (isPollingActive) {
      // ref를 사용한 동기적 중복 호출 방지
      if (isAffordanceActiveRef.current) {
        return;
      }
      
      // 어포던스 활성화 플래그 설정
      isAffordanceActiveRef.current = true;
      
      // 어포던스 타입 설정
      setAffordanceType(type);
      setIsSuccessAffordance(false);
      
      // 폴링 중일 때 어포던스 표시
      setShowAddressAffordance(true);
      setShowIconPulse(true);
      
      // 3초 후 어포던스 숨기기
      setTimeout(() => {
        setShowAddressAffordance(false);
      }, 3000);
      
      // 아이콘 펄스 효과 (1초)
      setTimeout(() => {
        setShowIconPulse(false);
      }, 1000);
      
      // 3초 후 플래그 해제 (어포던스가 완전히 끝난 후)
      setTimeout(() => {
        isAffordanceActiveRef.current = false;
      }, 3000);
      
      return;
    }
    
    // 폴링 중이 아니고 주소가 설정되지 않은 경우
    if (!isAddressSet()) {
      // Address 페이지의 경우에는 주소 설정을 위해 정상적으로 이동 허용
      if (type === 'address') {
        action();
        return;
      }
      
      // Cart 페이지의 경우에는 주소 설정이 필요함을 알림
      // ref를 사용한 동기적 중복 호출 방지
      if (isAffordanceActiveRef.current) {
        return;
      }
      
      // 어포던스 활성화 플래그 설정
      isAffordanceActiveRef.current = true;
      
      // 어포던스 타입 설정
      setAffordanceType(type);
      setIsSuccessAffordance(false);
      
      // 주소가 설정되지 않은 경우 어포던스 표시
      setShowAddressAffordance(true);
      setShowIconPulse(true);
      
      // 3초 후 어포던스 숨기기
      setTimeout(() => {
        setShowAddressAffordance(false);
      }, 3000);
      
      // 아이콘 펄스 효과 (1초)
      setTimeout(() => {
        setShowIconPulse(false);
      }, 1000);
      
      // 3초 후 플래그 해제 (어포던스가 완전히 끝난 후)
      setTimeout(() => {
        isAffordanceActiveRef.current = false;
      }, 3000);
      
      return;
    }
    
    // 주소가 설정된 경우 액션 실행
    action();
  }, [isAddressSet, isPollingActive]);

  // 주소 세팅 성공 어포던스 표시
  const showAddressSetSuccessAffordance = useCallback(() => {
    // 중복 호출 방지
    if (isAffordanceActiveRef.current) {
      return;
    }
    
    // 어포던스 활성화 플래그 설정
    isAffordanceActiveRef.current = true;
    
    // 성공 어포던스 설정
    setIsSuccessAffordance(true);
    setAffordanceType('address'); // 성공 어포던스는 항상 address 타입
    
    // 어포던스 표시
    setShowAddressAffordance(true);
    setShowIconPulse(true);
    
    // 4초 후 어포던스 숨기기 (성공 메시지는 조금 더 오래)
    setTimeout(() => {
      setShowAddressAffordance(false);
      setIsSuccessAffordance(false);
    }, 4000);
    
    // 아이콘 펄스 효과 (1초)
    setTimeout(() => {
      setShowIconPulse(false);
    }, 1000);
    
    // 4초 후 플래그 해제
    setTimeout(() => {
      isAffordanceActiveRef.current = false;
    }, 4000);
  }, []);

  // 어포던스 효과 수동 해제
  const dismissAffordance = useCallback(() => {
    setShowAddressAffordance(false);
    setShowIconPulse(false);
    setIsSuccessAffordance(false);
    isAffordanceActiveRef.current = false;
  }, []);

  const value = {
    showAddressAffordance,
    showIconPulse,
    checkAddressAndExecute,
    dismissAffordance,
    isAddressSet: isAddressSet(),
    isPollingActive,
    affordanceType,
    showAddressSetSuccessAffordance,
    isSuccessAffordance
  };

  return (
    <AddressAffordanceContext.Provider value={value}>
      {children}
    </AddressAffordanceContext.Provider>
  );
};

// Hook
export const useAddressAffordance = () => {
  const context = useContext(AddressAffordanceContext);
  if (context === undefined) {
    throw new Error('useAddressAffordance must be used within an AddressAffordanceProvider');
  }
  return context;
};
